(() => {
  if (window.vh12HeroFilmController) { window.vh12HeroFilmController.refresh(); return; }
  const root = document.documentElement;
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const touchPreference = window.matchMedia('(pointer: coarse)');
  const compactPreference = window.matchMedia('(max-width: 649px), (max-height: 649px)');
  const states = new Map();
  const motionAllowed = () => root.classList.contains('vh12-motion-ready') &&
    !root.classList.contains('vh12-motion-paused') &&
    (!preference.matches || root.classList.contains('vh12-motion-opt-in')) && !document.hidden;
  const allowed = state => !state.destroyed && state.visible && state.frame.isConnected && motionAllowed();
  const preferredSource = state => {
    if (touchPreference.matches) {
      if (compactPreference.matches && !state.phoneFailed && state.video.dataset.vh12FilmPhoneSrc) return state.video.dataset.vh12FilmPhoneSrc;
      if (!state.touchFailed && state.video.dataset.vh12FilmTouchSrc) return state.video.dataset.vh12FilmTouchSrc;
    }
    return state.video.dataset.vh12FilmSrc;
  };
  const updateHeadline = (state, time = state.started ? state.video.currentTime : null) => {
    if (state.destroyed) return;
    if (window.vh12MotionController?.setHeroFilmTime) window.vh12MotionController.setHeroFilmTime(state.frame, time);
  };
  const cancelClock = state => {
    if (state.clockFrame !== null) cancelAnimationFrame(state.clockFrame);
    state.clockFrame = null;
  };
  const sampleClock = state => {
    if (state.clockFrame !== null || !allowed(state) || state.video.paused || state.failed || state.waiting || state.switching) return;
    updateHeadline(state);
    state.clockFrame = requestAnimationFrame(() => {
      state.clockFrame = null;
      sampleClock(state);
    });
  };
  const pauseGap = state => {
    if (state.gapTimer !== null) {
      state.gapRemaining = Math.max(0, state.gapDue - performance.now());
      clearTimeout(state.gapTimer);
      state.gapTimer = null;
    }
  };
  const scheduleGap = state => {
    if (!state.waiting || state.gapTimer !== null || !allowed(state) || state.failed) return;
    state.gapDue = performance.now() + state.gapRemaining;
    state.gapTimer = setTimeout(() => {
      state.gapTimer = null;
      state.gapRemaining = 0;
      if (!allowed(state) || state.failed) return;
      state.waiting = false;
      state.video.currentTime = 0;
      play(state);
    }, state.gapRemaining);
  };
  const stop = state => {
    cancelClock(state);
    pauseGap(state);
    state.request += 1;
    state.pending = false;
    state.video.pause();
    if (preference.matches && !root.classList.contains('vh12-motion-opt-in')) state.frame.classList.remove('vh12-film-ready');
  };
  const fallback = (state, failed) => {
    cancelClock(state);
    updateHeadline(state, null);
    pauseGap(state);
    state.waiting = false;
    state.failed = failed;
    state.blocked = !failed;
    state.pending = false;
    state.frame.classList.remove('vh12-film-ready');
    state.frame.classList.toggle('vh12-film-blocked', !failed);
    state.retry.hidden = failed;
    state.status.textContent = failed ? 'The still portrait is shown because the film could not load.' : 'Automatic film playback is unavailable. Playing the film is optional.';
  };
  const sourceMatches = state => !state.video.currentSrc || state.video.currentSrc === state.source;
  const finishSourceChange = state => {
    if (state.destroyed || !state.switching || !sourceMatches(state)) return;
    state.switching = false;
    state.metadataSource = null;
    if (state.started) updateHeadline(state);
    if (state.started && state.video.readyState >= 2 && (!preference.matches || root.classList.contains('vh12-motion-opt-in'))) state.frame.classList.add('vh12-film-ready');
    if (allowed(state)) play(state);
  };
  const changeSource = state => {
    const next = preferredSource(state);
    if (state.destroyed || state.failed || !state.video.getAttribute('src') || next === state.source) return;
    // Keep time, pause intent and the remaining intermission across an input-device change.
    if (!state.switching) state.restoreTime = Number.isFinite(state.video.currentTime) ? state.video.currentTime : 0;
    stop(state);
    state.switching = true;
    state.metadataSource = null;
    state.source = next;
    state.frame.classList.remove('vh12-film-ready');
    state.video.src = next;
    state.video.load();
  };
  const play = (state, manual = false) => {
    if (state.switching) return;
    if (state.waiting) { scheduleGap(state); return; }
    if (!allowed(state) || state.failed || state.pending || (!manual && state.blocked) || !state.video.paused) return;
    if (!state.video.getAttribute('src')) {
      state.source = preferredSource(state);
      state.video.src = state.source;
      state.video.load();
    }
    state.video.muted = true;
    state.pending = true;
    const request = ++state.request;
    Promise.resolve(state.video.play()).then(() => {
      if (request !== state.request || !allowed(state)) {
        if (!allowed(state)) state.video.pause();
        if (request === state.request) state.pending = false;
        return;
      }
      state.pending = false;
      state.blocked = false;
      state.retry.hidden = true;
      state.frame.classList.remove('vh12-film-blocked');
      state.status.textContent = '';
    }).catch(() => {
      if (request !== state.request) return;
      state.pending = false;
      if (allowed(state)) fallback(state, false);
    });
  };
  const sync = () => states.forEach(state => {
    if (allowed(state)) play(state); else stop(state);
  });
  const initialize = () => {
    states.forEach((state, frame) => {
      if (!frame.isConnected) { state.destroyed = true; stop(state); window.vh12MotionController?.releaseHeroFilmClock?.(frame); if (state.observer) state.observer.disconnect(); states.delete(frame); }
    });
    document.querySelectorAll('[data-vh12-film-frame]').forEach(frame => {
      if (states.has(frame)) return;
      const video = frame.querySelector('[data-vh12-film-src]');
      const toggle = frame.querySelector('[data-vh12-motion-toggle]');
      const retry = frame.querySelector('[data-vh12-film-retry]');
      const status = frame.querySelector('[data-vh12-film-status]');
      if (!video || !toggle || !retry || !status) return;
      const state = { frame, video, toggle, retry, status, visible: false, pending: false, blocked: false, failed: false, destroyed: false, request: 0, waiting: false, gapTimer: null, gapRemaining: 3000, gapDue: 0, started: false, clockFrame: null, source: null, switching: false, restoreTime: 0, metadataSource: null, touchFailed: false, phoneFailed: false };
      states.set(frame, state);
      updateHeadline(state, null);
      toggle.hidden = false;
      video.muted = true;
      video.autoplay = false; // The controller respects pause and reduced motion during source changes.
      video.addEventListener('playing', () => {
        if (allowed(state) && !state.switching) { state.started = true; frame.classList.add('vh12-film-ready'); sampleClock(state); } else stop(state);
      });
      video.addEventListener('ended', () => {
        if (state.destroyed || state.switching) return;
        cancelClock(state);
        updateHeadline(state);
        state.request += 1;
        state.pending = false;
        pauseGap(state);
        state.waiting = true;
        state.gapRemaining = 3000;
        scheduleGap(state);
      });
      video.addEventListener('timeupdate', () => { if (allowed(state) && state.started && !state.failed && !state.switching) updateHeadline(state); });
      video.addEventListener('waiting', () => cancelClock(state));
      video.addEventListener('pause', () => cancelClock(state));
      video.addEventListener('loadedmetadata', () => {
        if (state.destroyed || !state.switching || !sourceMatches(state)) return;
        state.metadataSource = state.source;
        const limit = Number.isFinite(video.duration) ? Math.max(0, video.duration - .001) : state.restoreTime;
        const target = Math.min(Math.max(0, state.restoreTime), limit);
        if (Math.abs((video.currentTime || 0) - target) < .001) finishSourceChange(state);
        else video.currentTime = target;
      });
      video.addEventListener('seeked', () => {
        if (state.metadataSource === state.source && !video.seeking) finishSourceChange(state);
      });
      video.addEventListener('error', () => {
        if (state.destroyed) return;
        const phoneSourceFailed = state.source === video.dataset.vh12FilmPhoneSrc;
        const touchSourceFailed = state.source === video.dataset.vh12FilmTouchSrc;
        if ((phoneSourceFailed || touchSourceFailed) && state.source !== video.dataset.vh12FilmSrc) {
          state.phoneFailed = state.phoneFailed || phoneSourceFailed;
          state.touchFailed = state.touchFailed || touchSourceFailed;
          changeSource(state);
        } else { stop(state); fallback(state, true); }
      });
      retry.addEventListener('click', () => {
        state.blocked = false;
        if (!motionAllowed()) toggle.click();
        play(state, true);
      });
      if ('IntersectionObserver' in window) {
        state.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => { if (entry.target === frame) state.visible = entry.isIntersecting; });
          sync();
        }, { threshold: 0.12 });
        state.observer.observe(frame);
      } else {
        const bounds = frame.getBoundingClientRect();
        state.visible = bounds.bottom > 0 && bounds.top < window.innerHeight;
      }
    });
    sync();
  };
  const viewportFallback = () => {
    if ('IntersectionObserver' in window) return;
    states.forEach(state => { const box = state.frame.getBoundingClientRect(); state.visible = box.bottom > 0 && box.top < window.innerHeight; });
    sync();
  };
  const inputChanged = () => states.forEach(changeSource);
  if (touchPreference.addEventListener) touchPreference.addEventListener('change', inputChanged);
  else if (touchPreference.addListener) touchPreference.addListener(inputChanged);
  if (compactPreference.addEventListener) compactPreference.addEventListener('change', inputChanged);
  else if (compactPreference.addListener) compactPreference.addListener(inputChanged);
  new MutationObserver(sync).observe(root, { attributes: true, attributeFilter: ['class'] });
  document.addEventListener('visibilitychange', sync);
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    states.forEach((state, frame) => {
      if (!frame.isConnected || (event.target && event.target.contains(frame))) {
        state.destroyed = true;
        stop(state);
        window.vh12MotionController?.releaseHeroFilmClock?.(frame);
        if (state.observer) state.observer.disconnect();
        states.delete(frame);
      }
    });
  });
  window.addEventListener('scroll', viewportFallback, { passive: true });
  window.addEventListener('resize', viewportFallback, { passive: true });
  if (preference.addEventListener) preference.addEventListener('change', sync);
  else if (preference.addListener) preference.addListener(sync);
  window.vh12HeroFilmController = { refresh: initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();

